import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AvatarUsuario from './AvatarUsuario';
import '../styles/MenuAvatar.css';

// Menu do avatar, no topo (components/Navbar.js). O Perfil saiu do menu lateral
// e mora aqui, em todos os papéis e também no celular. Teclado: Enter/Espaço ou
// seta para baixo abrem e focam o primeiro item; setas percorrem; Esc fecha e
// devolve o foco ao avatar. Com avatar escolhido mostra a imagem; sem ele, as
// iniciais com a cor da pessoa (components/AvatarUsuario.js).
const MenuAvatar = ({ usuario, onAbrir }) => {
  const nome = usuario?.nome;
  const navigate = useNavigate();
  const { t } = useTranslation('navbar');
  const [aberto, setAberto] = useState(false);
  const raizRef = useRef(null);
  const botaoRef = useRef(null);
  const menuRef = useRef(null);
  const focarAoAbrir = useRef(false);

  const itens = () => (menuRef.current ? Array.from(menuRef.current.querySelectorAll('[role="menuitem"]')) : []);

  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => {
      if (raizRef.current && !raizRef.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    if (focarAoAbrir.current) {
      focarAoAbrir.current = false;
      const [primeiro] = itens();
      if (primeiro) primeiro.focus();
    }
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const abrir = (focar) => {
    focarAoAbrir.current = focar;
    if (onAbrir) onAbrir();
    setAberto(true);
  };

  const fechar = (devolverFoco) => {
    setAberto(false);
    if (devolverFoco && botaoRef.current) botaoRef.current.focus();
  };

  const irPara = (rota) => {
    fechar(false);
    navigate(rota);
  };

  const aoTeclarBotao = (e) => {
    if (e.key === 'ArrowDown' || ((e.key === 'Enter' || e.key === ' ') && !aberto)) {
      e.preventDefault();
      abrir(true);
    } else if (e.key === 'Escape' && aberto) {
      e.preventDefault();
      fechar(true);
    }
  };

  const aoTeclarMenu = (e) => {
    const lista = itens();
    const atual = lista.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const passo = e.key === 'ArrowDown' ? 1 : -1;
      const proximo = lista[(atual + passo + lista.length) % lista.length];
      if (proximo) proximo.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      fechar(true);
    } else if (e.key === 'Tab') {
      setAberto(false);
    }
  };

  return (
    <div className="menu-avatar" ref={raizRef}>
      <button
        type="button"
        ref={botaoRef}
        className="user-action avatar-button"
        onClick={() => (aberto ? fechar(false) : abrir(false))}
        onKeyDown={aoTeclarBotao}
        title={t('accountMenu')}
        aria-label={t('accountMenu')}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? 'menu-avatar-lista' : undefined}
      >
        {/* O botão já tem rótulo (menu da conta): a imagem é decorativa aqui. */}
        <AvatarUsuario usuario={usuario} tamanho={26} className="user-photo" decorativo />
      </button>

      {aberto && (
        <div
          id="menu-avatar-lista"
          ref={menuRef}
          className="dropdown-menu menu-avatar-lista"
          role="menu"
          aria-label={t('accountMenu')}
          onKeyDown={aoTeclarMenu}
        >
          {nome && <div className="menu-avatar-nome" aria-hidden="true">{nome}</div>}
          <button type="button" role="menuitem" onClick={() => irPara('/perfil')}>
            <span aria-hidden="true">👤</span> {t('profile')}
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuAvatar;
